import { Button, Divider, Icon, Radio } from '@kontentino/ui';
import { FC, useEffect, useState } from 'react';
import { useMutation, useQuery } from 'react-query';

import Modal from 'components/shared/Modal';
import Link from 'components/shared/Link';
import Skeleton from 'react-loading-skeleton';
import { queryClient } from 'api/client';
import { queryKey } from 'constants/queryKey';
import { createSearchFromParams, routePathToUrl } from 'utils/url';
import routes, { modalRoutes } from 'constants/routes';
import ChangeValueDataCell from './components/ChangeValueDataCell';
import ChangeHeaderCell from './components/ChangeHeaderCell';
import { PostPatch, PostPatchStatus } from 'types/PostDetail';
import ArrayUtils from 'app/utils/array';
import { useToast } from 'app/hooks/useToast';
import { faInfoCircle } from '@fortawesome/pro-regular-svg-icons/faInfoCircle';
import { UserPermission } from 'constants/userPermission';
import { parsePendingChangeToUserMessage } from 'app/modules/posts/utils/parsePendingChangeToUserMessage';
import Logger from 'utils/logger';
import UserPermissionGate from 'components/UserPermissionGate';
import { formatPatchesToRequestData } from 'app/modules/posts/utils/formatPatchesToRequestData';
import { useTranslation } from 'react-i18next';
import PostApi from 'api/post';

type Props = {
  postId: number;
  isOpen: boolean;
  onClose: () => void;
  onApplySuccess?: () => void;
};

const PostPendingChangesDetailModal: FC<Props> = ({
  postId,
  isOpen,
  onClose,
  onApplySuccess,
}) => {
  const toast = useToast();
  const [patches, setPatches] = useState<
    (PostPatch & { status: PostPatchStatus })[]
  >([]);
  const [headerStatus, setHeaderStatus] = useState<PostPatchStatus | null>(
    null,
  );
  const { t } = useTranslation();

  const STATUS_LABEL: Record<PostPatchStatus, string> = {
    apply: t('globalPages.pendingChanges.apply'),
    skip: t('globalPages.pendingChanges.skip'),
    not_now: t('globalPages.pendingChanges.notNow'),
  };

  const { isLoading } = useQuery(
    queryKey.postPatches(postId),
    () => PostApi.getPatches(postId),
    {
      enabled: isOpen,
      onSuccess: (data) => {
        setPatches(
          data.map((patch) => ({
            ...patch,
            status: 'apply',
          })),
        );
      },
      onError: (error) => {
        Logger.error(error);
        toast(t('globalPages.pendingChanges.failedToFetchPatches'), 'error');
      },
    },
  );

  const handleCheckboxChange = (id: number, status: PostPatchStatus) => {
    setPatches((prev) =>
      prev.map((patch) => (patch.id === id ? { ...patch, status } : patch)),
    );
  };

  const handleHeaderCheckboxChange = (status: PostPatchStatus) => {
    setHeaderStatus(status);
    setPatches((prev) =>
      prev.map((patch) => ({
        ...patch,
        status,
      })),
    );
  };

  useEffect(() => {
    const allApply = patches.every((patch) => patch.status === 'apply');
    const allSkip = patches.every((patch) => patch.status === 'skip');
    const allNotNow = patches.every((patch) => patch.status === 'not_now');

    setHeaderStatus(() => {
      if (allApply) {
        return 'apply';
      } else if (allSkip) {
        return 'skip';
      } else if (allNotNow) {
        return 'not_now';
      } else {
        return null;
      }
    });
  }, [patches]);

  const applyPatchesMutation = useMutation(
    async () => {
      await PostApi.applyPatches(postId, formatPatchesToRequestData(patches));
    },
    {
      onSuccess: () => {
        toast(
          t('globalPages.pendingChanges.changesAppliedSuccessfully'),
          'success',
        );
        queryClient.invalidateQueries(queryKey.postDetail(postId));
        queryClient.invalidateQueries(queryKey.postScheduleValidation(postId));
        queryClient.invalidateQueries(queryKey.postTranslation(postId));
        onApplySuccess?.();
        onClose();
      },
      onError: () => {
        toast(t('globalPages.pendingChanges.failedToApplyPatches'), 'error');
      },
    },
  );

  return (
    <Modal size="md" open={isOpen} onClose={onClose}>
      <Modal.Header
        title={
          <div>
            <h2>{t('globalPages.pendingChanges.title')}</h2>
            <p className="tw-text-md tw-font-regular">
              {t('globalPages.pendingChanges.description')}
            </p>
          </div>
        }
      />
      <Modal.Content className="tw-pb-6">
        <Link
          target="_blank"
          to={routePathToUrl({
            pathname: routes.POSTS_CALENDAR,
            hash: modalRoutes.POST_DETAIL,
            search: createSearchFromParams({ id: postId }),
          })}
        >
          <Button variant="secondary" className="tw-mb-4">
            {t('globalPages.viewGlobalPost')}
          </Button>
        </Link>

        <div className="tw-relative tw-mb-6 tw-mt-4 tw-flex tw-h-full tw-w-full tw-flex-col tw-overflow-scroll tw-bg-white">
          <table className="tw-w-full tw-min-w-max tw-table-auto tw-text-left">
            <thead>
              <tr>
                <ChangeHeaderCell>
                  {t('globalPages.pendingChanges.change')}
                </ChangeHeaderCell>
                {Object.entries(STATUS_LABEL).map(([status, label]) => (
                  <ChangeHeaderCell key={status}>
                    <Radio
                      id={`header-status-${status}`}
                      size="small"
                      checked={headerStatus === status}
                      onChange={() =>
                        handleHeaderCheckboxChange(status as PostPatchStatus)
                      }
                    />
                    <label htmlFor={`header-status-${status}`}>{label}</label>
                  </ChangeHeaderCell>
                ))}
              </tr>
            </thead>

            <tbody className="tw-w-full">
              {isLoading
                ? ArrayUtils.generate(5).map((i) => (
                    <tr key={i}>
                      <td className="w-1/3">
                        <Skeleton count={1} height={36} width={'100%'} />
                      </td>
                      {ArrayUtils.generate(3).map((j) => (
                        <td key={j} className="w-1/3" style={{ width: '36px' }}>
                          <Skeleton count={1} height={36} width={'100%'} />
                        </td>
                      ))}
                    </tr>
                  ))
                : patches.map((patch) => (
                    <tr
                      className="tw-mb-2 hover:tw-bg-grayscale-5"
                      key={patch.id}
                    >
                      <ChangeValueDataCell>
                        {parsePendingChangeToUserMessage(patch.postColumn)}
                      </ChangeValueDataCell>

                      {(['apply', 'skip', 'not_now'] as PostPatchStatus[]).map(
                        (status) => (
                          <ChangeValueDataCell key={status}>
                            <Radio
                              id={`patch-${patch.id}-status-${status}`}
                              size="small"
                              checked={patch.status === status}
                              onChange={() =>
                                handleCheckboxChange(patch.id, status)
                              }
                            />
                            <label
                              htmlFor={`patch-${patch.id}-status-${status}`}
                            >
                              {STATUS_LABEL[status]}
                            </label>
                          </ChangeValueDataCell>
                        ),
                      )}
                    </tr>
                  ))}
            </tbody>
          </table>
        </div>
        <div className="tw-flex tw-gap-1">
          <Icon
            icon={faInfoCircle}
            size={16}
            className="tw-mr-2 tw-text-grayscale-100"
          />
          <div className="tw-text-sm tw-font-regular tw-text-grayscale-100">
            <p>{t('globalPages.pendingChanges.applyChangesToPost')}</p>
            <p>{t('globalPages.pendingChanges.skipChangesToPost')}</p>
            <p>{t('globalPages.pendingChanges.notNowApplyChangesLater')}</p>
          </div>
        </div>
      </Modal.Content>
      <Divider />
      <Modal.Footer>
        <Button className="tw-w-full" variant="secondary" onClick={onClose}>
          {t('globalPages.pendingChanges.cancel')}
        </Button>
        <UserPermissionGate
          scopes={[UserPermission.POST_APPLY_GLOBAL_CHANGES]}
          deniedProps={{ disabled: true }}
        >
          <Button
            onClick={() => applyPatchesMutation.mutate()}
            className="tw-w-full"
            variant="primary"
            isLoading={applyPatchesMutation.isLoading}
          >
            {t('globalPages.pendingChanges.confirm')}
          </Button>
        </UserPermissionGate>
      </Modal.Footer>
    </Modal>
  );
};

export default PostPendingChangesDetailModal;
